import { okResponse } from '@crow/service-kit/web/api';
import type { PluginListingEntry } from '@crowdex/agent-contracts/api';
import { apiRefusals } from '@crowdex/host-kit/renderer/http';
import type { AppearanceVariant } from '@crowdex/ui/lib/appearance/chrome-theme';
import type { CodeTheme } from '@crowdex/ui/lib/appearance/code-themes';
import { parseVscodeTheme, type VscodeTheme } from '@crowdex/ui/lib/appearance/vscode-theme';
import { useSyncExternalStore } from 'react';
import { bundledThemes, bundledThemesInfo } from 'shiki';
import { readDevicePreference, writeDevicePreference } from '@/lib/device-preferences';
import { CROWDEX_CODE_THEME } from './appearance';

/**
 * Where a code theme comes from: the app's own, one Shiki bundles (by its id), one the user
 * imported (`imported:<slug>`), or one an enabled plugin contributes (`plugin:<name>/<id>`).
 */
export type CodeThemeOption = Readonly<{
  id: string;
  label: string;
  type: AppearanceVariant;
  source: 'crowdex' | 'bundled' | 'imported' | 'plugin';
}>;

const CROWDEX_SHIKI: Record<AppearanceVariant, string> = {
  light: 'one-light',
  dark: 'one-dark-pro',
};

const IMPORTED_THEMES_KEY = 'crowdex.appearance.importedThemes';
const MAX_IMPORTED_THEMES = 24;

type ImportedTheme = { id: string; theme: VscodeTheme };

// Bumped when an import replaces or removes a theme, so whoever shows it loads it again.
let importedRevision = 0;
const importedListeners = new Set<() => void>();

function importedThemesChanged() {
  importedRevision += 1;
  for (const listener of importedListeners) listener();
}

/** Changes whenever the imported themes do. */
export function useImportedThemesRevision(): number {
  return useSyncExternalStore(
    (listener) => {
      importedListeners.add(listener);
      return () => importedListeners.delete(listener);
    },
    () => importedRevision,
    () => importedRevision,
  );
}

export function readImportedThemes(): ImportedTheme[] {
  try {
    const parsed: unknown = JSON.parse(readDevicePreference(IMPORTED_THEMES_KEY) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry) => {
      const id = (entry as { id?: unknown })?.id;
      if (typeof id !== 'string' || !id.startsWith('imported:')) return [];
      try {
        return [{ id, theme: parseVscodeTheme((entry as { theme?: unknown }).theme) }];
      } catch {
        return [];
      }
    });
  } catch {
    return [];
  }
}

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 48) || 'theme'
  );
}

/** Keeps a VS Code theme the user picked from a file; a theme of the same name is replaced. */
export async function importVscodeTheme(
  content: string,
  fileName: string,
): Promise<CodeThemeOption> {
  const theme = parseVscodeTheme(content, fileName.replace(/\.(json|jsonc)$/i, ''));
  const id = `imported:${slug(theme.name)}`;
  const themes = [{ id, theme }, ...readImportedThemes().filter((entry) => entry.id !== id)].slice(
    0,
    MAX_IMPORTED_THEMES,
  );
  await writeDevicePreference(IMPORTED_THEMES_KEY, JSON.stringify(themes));
  resolved.delete(id);
  importedThemesChanged();
  return { id, label: theme.name, type: theme.type, source: 'imported' };
}

export async function removeImportedTheme(id: string): Promise<void> {
  const themes = readImportedThemes().filter((entry) => entry.id !== id);
  await writeDevicePreference(IMPORTED_THEMES_KEY, JSON.stringify(themes));
  resolved.delete(id);
  importedThemesChanged();
}

/** An imported theme's VS Code data, to carry it to another device. */
export function importedTheme(id: string): VscodeTheme | null {
  return readImportedThemes().find((entry) => entry.id === id)?.theme ?? null;
}

/** The themes enabled plugins contribute, by their code theme id. */
export function pluginCodeThemes(plugins: readonly PluginListingEntry[]): (CodeThemeOption & {
  url: string;
})[] {
  return plugins
    .filter((plugin) => plugin.enabled && plugin.blocked === undefined)
    .flatMap((plugin) =>
      (plugin.themes ?? []).map((theme) => ({
        id: `plugin:${plugin.name}/${theme.id}`,
        label: theme.label,
        type: theme.type,
        source: 'plugin' as const,
        url: theme.url,
      })),
    );
}

/** Every code theme the variant can use, the app's own first. */
export function codeThemeOptions(
  variant: AppearanceVariant,
  pluginThemes: readonly CodeThemeOption[] = [],
): CodeThemeOption[] {
  const bundled = bundledThemesInfo
    .filter((theme) => theme.type === variant)
    .map((theme) => ({
      id: theme.id,
      label: theme.displayName,
      type: variant,
      source: 'bundled' as const,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const imported = readImportedThemes()
    .filter((entry) => entry.theme.type === variant)
    .map((entry) => ({
      id: entry.id,
      label: entry.theme.name,
      type: variant,
      source: 'imported' as const,
    }));
  return [
    { id: CROWDEX_CODE_THEME, label: 'Crowdex', type: variant, source: 'crowdex' },
    ...pluginThemes.filter((theme) => theme.type === variant),
    ...imported,
    ...bundled,
  ];
}

const resolved = new Map<string, Promise<VscodeTheme>>();

async function loadCodeTheme(
  id: string,
  variant: AppearanceVariant,
  pluginThemes: readonly (CodeThemeOption & { url?: string })[],
): Promise<VscodeTheme> {
  const bundledId = id === CROWDEX_CODE_THEME ? CROWDEX_SHIKI[variant] : id;
  if (id.startsWith('imported:')) {
    const entry = readImportedThemes().find((theme) => theme.id === id);
    if (!entry) throw new Error('This imported theme is no longer on this device.');
    return entry.theme;
  }
  if (id.startsWith('plugin:')) {
    const option = pluginThemes.find((theme) => theme.id === id);
    if (!option?.url) throw new Error('No enabled plugin offers this theme.');
    // A plugin's file, not JSON of the API: read as text, a refusal read as the API's.
    const response = await okResponse(await fetch(option.url, { credentials: 'same-origin' }), {
      ...apiRefusals,
      fallbackMessage: (status) => `Could not load the theme (HTTP ${status}).`,
    });
    return parseVscodeTheme(await response.text(), option.label, option.type);
  }
  const load = bundledThemes[bundledId as keyof typeof bundledThemes];
  if (!load) throw new Error(`Unknown code theme: ${id}`);
  return parseVscodeTheme((await load()).default);
}

/**
 * Under what a code theme is cached: a plugin's by its URL too, which carries the build's
 * digest, so an updated plugin's theme is fetched again.
 */
export function codeThemeCacheKey(
  id: string,
  variant: AppearanceVariant,
  pluginThemes: readonly (CodeThemeOption & { url?: string })[] = [],
): string {
  if (id === CROWDEX_CODE_THEME) return `${id}:${variant}`;
  if (id.startsWith('plugin:'))
    return `${id}@${pluginThemes.find((theme) => theme.id === id)?.url ?? ''}`;
  return id;
}

/** A code theme's VS Code data, loaded once per id (and plugin build). */
export function resolveCodeTheme(
  id: string,
  variant: AppearanceVariant,
  pluginThemes: readonly (CodeThemeOption & { url?: string })[] = [],
): Promise<VscodeTheme> {
  const key = codeThemeCacheKey(id, variant, pluginThemes);
  let theme = resolved.get(key);
  if (!theme) {
    theme = loadCodeTheme(id, variant, pluginThemes);
    resolved.set(key, theme);
    theme.catch(() => resolved.delete(key));
  }
  return theme;
}

/**
 * What the code highlighter takes for a code theme: Shiki's own id when it bundles it, otherwise
 * the theme as data, named by its id so Shiki's caches keep imported themes apart.
 */
export function shikiCodeTheme(
  id: string,
  variant: AppearanceVariant,
  theme: VscodeTheme | null,
): CodeTheme {
  if (id === CROWDEX_CODE_THEME) return CROWDEX_SHIKI[variant] as CodeTheme;
  if (id in bundledThemes) return id as CodeTheme;
  if (!theme) return CROWDEX_SHIKI[variant] as CodeTheme;
  return {
    name: id,
    displayName: theme.name,
    type: theme.type,
    colors: { ...theme.colors },
    tokenColors: [...theme.tokenColors],
    ...(theme.semanticHighlighting === undefined
      ? {}
      : { semanticHighlighting: theme.semanticHighlighting }),
    ...(theme.semanticTokenColors === undefined
      ? {}
      : { semanticTokenColors: { ...theme.semanticTokenColors } }),
  } as CodeTheme;
}
